/**
 * Interactive walkthrough ("coach marks"): a sequence of steps, each pointing at a part of the UI
 * and asking the user to try something. A step completes when the editor state shows that they did
 * (typed, entered a formula, started a comment, opened the Versions tab, …) — or when they skip it;
 * the whole tour can be left at any time. It is offered once per browser (`localStorage.ol.tour`)
 * and can be restarted from Help ▸ Take the tour or the start screen.
 *
 * The tour never blocks the interface: the dimmed overlay lets clicks through, only the card itself
 * takes them (without stealing the editor's focus), so the user works in the real editor.
 */
import { useUiMode, setUiMode, uiMode } from './uimode';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';

const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = isMac ? '⌘' : 'Ctrl';
// ⌘M (minimise) and ⌘⌥C (DevTools) belong to macOS/the browser before the page sees them:
// the editor also binds the Ctrl variants there, and the tour asks for those
const MATH_KEY = isMac ? '⌃M' : 'Ctrl+M';
const COMMENT_KEY = isMac ? '⌃⌥C' : 'Ctrl+Alt+C';

/** Live state of the workspace the steps look at. */
export interface TourCtx {
  docId: string | null;
  /** a LyX document is open, synced and its editor is on screen */
  ready: boolean;
  /** bumps on every document change */
  docTick: number;
  /** layout of the paragraph under the cursor */
  layout: string;
  /** the cursor is inside a formula */
  inMath: boolean;
  saveState: string;
  rightTab: string | null;
  pdfBusy: boolean;
  pdfBuiltAt: number;
  shareOpen: boolean;
  gitOpen: boolean;
  marginMode: boolean;
}
export interface TourActions {
  /** open the user's example document (or another document to practise on); resolves false if there is none */
  openExample: () => Promise<boolean>;
  showRight: () => void;
  /** the PDF pane beside the text */
  showPdf: () => void;
  showFiles: () => void;
}

/** ctx plus what the tour reads from the DOM itself */
interface Snap extends TourCtx { comments: number; /** paragraphs whose layout is not Standard */ styled: number }

interface Step {
  id: string;
  title: string;
  body: ComponentChildren | ((now: Snap, base: Snap | null) => ComponentChildren);
  /** CSS selectors tried in order; the first element found is highlighted */
  target?: string[];
  /** the task: complete when it holds, compared with the snapshot taken when the step became active */
  done?: (now: Snap, base: Snap) => boolean;
  /** the base snapshot is only taken once this holds (e.g. the document has loaded) */
  ready?: (now: Snap) => boolean;
  /** prepare the interface for the step */
  enter?: (a: TourActions) => void;
}

const kbd = (k: string) => <kbd>{k}</kbd>;

export const TOUR_STEPS: Step[] = [
  {
    id: 'type', title: 'A real LaTeX document',
    target: ['.lyx-editor'],
    ready: s => s.ready,
    body: (now) => now.ready ? <>
      <p>This example is yours to edit. Text and formulas render as you type, and the underlying <code>.tex</code> file opens in other LaTeX editors too.</p>
      <p>Use the PDF view to check the final page layout. LaTeX that the editor cannot render stays available in a <i>TeX Code</i> block.</p>
      <p><b>Click into the text and type a few words.</b></p>
    </> : <p>Opening your example document…</p>,
    done: (now, base) => now.docTick > base.docTick,
  },
  {
    id: 'layout', title: 'Paragraph styles',
    target: ['.toolbar-standard [data-style-picker]', '[data-style-picker]'],
    body: () => <>
      <p>Use paragraph styles to organize your text into headings, lists and theorems. This box shows the current paragraph's style; click it and type to search.</p>
      <p><b>Press {kbd('Enter')} for a new paragraph and make it a <i>Section</i></b> — pick it from this box or press {kbd(MOD + '+2')}{uiMode() === 'classic' && <> (or {kbd('Alt+P')} then {kbd('2')}, LyX's shortcut)</>}.</p>
    </>,
    // a new heading (or list item, …) appeared, or the paragraph under the cursor got another layout
    done: (now, base) => now.styled > base.styled || (now.docTick > base.docTick && now.layout !== base.layout && now.layout !== 'Standard'),
  },
  {
    id: 'math', title: 'Formulas, typeset as you type',
    target: ['[data-tb="math"]'],
    body: () => <>
      <p>Type LaTeX inside a formula{uiMode() === 'classic' && <> (LyX's math keys work too)</>}: {kbd('^')} and {kbd('_')} for scripts, {kbd('\\')} starts a command ({kbd('Tab')} completes it), {kbd('Esc')} leaves the formula. The math toolbar appears while the cursor is in one.</p>
      <p><b>Press {kbd(MATH_KEY)} (or this button) and type</b> <code>\alpha^2 + \beta</code>.</p>
    </>,
    done: now => now.inMath,
  },
  {
    id: 'save', title: 'Everything is saved for you',
    target: ['.menubar .save-state', '.statusbar .save-state'],
    body: (now) => <>
      <p>There is no Save button: every edit goes to the server at once and is written to the <code>.tex</code> file a moment later; this indicator turns green when the file is up to date. If the connection drops you keep editing offline and your changes sync when it is back.</p>
      <p><b>Wait for “All changes saved”.</b>{now.saveState === 'saved' && <> ✓</>}</p>
    </>,
    done: now => now.saveState === 'saved',
  },
  {
    id: 'comment', title: 'Comments and notes',
    target: ['[data-tb="comment"]'],
    body: <>
      <p>Comment threads (author, time, replies, resolve) are stored in the <code>.tex</code> file itself, so they travel with it. <i>View ▸ Notes &amp; comments in the margin</i> moves them next to the text, Google-Docs style.</p>
      <p><b>Select a word and press {kbd(COMMENT_KEY)}</b> (or this button) to start a thread, then type your remark.</p>
    </>,
    done: (now, base) => now.comments > base.comments,
  },
  {
    id: 'pdf', title: 'Compile to PDF',
    target: ['[data-tb="pdf"]'],
    enter: a => a.showPdf(),
    body: <>
      <p>PDF builds run in the background on the server (<code>latexmk</code>) — keep editing while they run. The <i>PDF</i> pane beside the text shows the progress, the result, how old it is and the log; once it is open it rebuilds by itself a moment after you stop typing (the ▾ beside <i>View PDF</i> sets that). <i>WYSIWYM · TeX · PDF</i> at the top arranges the panes.</p>
      <p><b>Press {kbd(MOD + '+R')}</b> or this button to build the PDF.</p>
    </>,
    done: (now, base) => now.pdfBusy || now.pdfBuiltAt !== base.pdfBuiltAt,
  },
  {
    id: 'versions', title: 'Versions and recovery',
    target: ['[data-tab="versions"]', '.sidebar.right .panel-tabs'],
    enter: a => a.showRight(),
    body: <>
      <p>The history of every document is kept: automatic snapshots, versions you name yourself, and the state before anything drastic (a large deletion, an external change, offline edits that could not be merged). Compare any of them with the current text and restore it.</p>
      <p><b>Open the <i>Versions</i> tab.</b></p>
    </>,
    done: now => now.rightTab === 'versions',
  },
  {
    id: 'share', title: 'Work together',
    target: ['.menubar [data-share]'],
    body: <>
      <p>Projects are private until you share them. Invite colleagues by e-mail or user name as <i>editors</i> or <i>viewers</i>, or turn on a link. Everybody edits the same document live — the avatars next to the Share button show who is here; click one to jump to their cursor.</p>
      <p><b>Click <i>Share</i> at the top right</b> (or <i>File ▸ Share project…</i>) to see the sharing dialog. You can close it again without inviting anyone.</p>
    </>,
    done: now => now.shareOpen,
  },
  {
    id: 'git', title: 'Your files stay yours',
    target: ['.menubar [data-main-menu]', '.menubar .menu > button'],
    body: () => <>
      <p>The project folder on the server is a git repository: <i>File ▸ Git repository…</i> shows the clone URL and creates access tokens, so you can <code>git clone</code>, edit with your favourite editor{uiMode() === 'classic' && <> or desktop LyX</>}, and <code>push</code> — OverLyX commits what people edit here and merges what you push. Every document is also mirrored in this browser, so it opens instantly and works offline.</p>
    </>,
  },
  {
    id: 'end', title: 'That’s the tour',
    body: () => <>
      <p>The example document itself goes on with figures, tables, citations, cross-references and math macros — edit it freely, it is yours. <i>Help ▸ Keyboard shortcuts</i> lists every shortcut{uiMode() === 'classic' ? <> — LyX's bindings ({kbd('Alt+P')} … paragraph layouts, {kbd('Alt+M')} … math, {kbd(MOD + '+Shift+E')} change tracking, …)</> : <> ({kbd(MOD + '+Shift+E')} change tracking, {kbd(MOD + '+F')} find, …)</>}.</p>
      <p>You can take this tour again any time from <i>Help ▸ Take the tour</i>.</p>
    </>,
  },
];

const countComments = () => document.querySelectorAll('.lyx-editor .lyx-inset-note-comment').length;
const countStyled = () => document.querySelectorAll('.lyx-editor > .lyx-par:not(.lyx-layout-standard)').length;

function findTarget(sels: string[] | undefined): HTMLElement | null {
  if (!sels) return null;
  for (const s of sels) {
    const el = document.querySelector<HTMLElement>(s);
    if (el && el.getBoundingClientRect().width > 0) return el;
  }
  return null;
}

export type TourEnd = 'finished' | 'left' | 'declined';

/** the two interfaces as small sketches: Classic (LyX's rows of icons, status bar) and Modern (Google's rounded row, a sheet) */
export function UiChoice() {
  const cur = useUiMode();
  // LyX: rows of coloured icons, everything in view
  const classic = (
    <svg viewBox="0 0 160 90" aria-hidden="true">
      <rect width="160" height="90" fill="#f3f3f1" />
      <rect y="0" width="160" height="9" fill="#e9e9e6" />{[6, 22, 38, 54].map(x => <rect key={x} x={x} y="3" width="11" height="3" rx="1" fill="#888" />)}
      <rect y="10" width="160" height="9" fill="#f3f3f1" />{Array.from({ length: 22 }, (_, i) => <rect key={i} x={4 + i * 7} y="12" width="5" height="5" rx="1" fill={['#d9a33b', '#5b8dc9', '#7a9a52', '#b0b0b0'][i % 4]} />)}
      <rect y="20" width="160" height="9" fill="#f3f3f1" />{Array.from({ length: 18 }, (_, i) => <rect key={i} x={4 + i * 8} y="22" width="5" height="5" rx="1" fill={['#5b8dc9', '#b0b0b0', '#c9645b'][i % 3]} />)}
      <rect x="0" y="30" width="28" height="52" fill="#f8f8f6" /><rect x="132" y="30" width="28" height="52" fill="#fff" />
      <rect x="30" y="30" width="100" height="52" fill="#fff" />{[36, 44, 52, 60, 68].map(y => <rect key={y} x="40" y={y} width={y === 36 ? 50 : 80} height="3" rx="1" fill="#bbb" />)}
      <rect y="83" width="160" height="7" fill="#e9e9e6" />
    </svg>
  );
  // Modern: one header row (the wordmark, the menu button, the name), one strip of keys, the page as a sheet
  const modern = (
    <svg viewBox="0 0 160 90" aria-hidden="true">
      <rect width="160" height="90" fill="#f1efea" />
      {[5.6, 8.2, 10.8].map((y, k) => <rect key={y} x={k === 1 ? 4 : 5.5} y={y} width={k === 1 ? 5 : 3.5} height="1.1" fill="#1c1b19" />)}
      <rect x="11" y="5.4" width="9" height="5.6" rx="0.6" fill="#1c1b19" />
      <rect x="20.8" y="5" width="2.2" height="6" fill="#2152a3" transform="rotate(-12 21.9 8)" /><rect x="23.6" y="6" width="2.2" height="6" fill="#efb21a" transform="rotate(7 24.7 9)" /><rect x="26.4" y="5" width="2.2" height="6" fill="#d6402b" transform="rotate(12 27.5 8)" />
      {[6.2, 8.2, 10.2].map(y => <rect key={y} x="34" y={y} width="5" height="0.9" rx="0.4" fill="#4a4741" />)}
      <rect x="43" y="6" width="30" height="4" rx="1" fill="#1c1b19" />
      <rect x="132" y="4.5" width="22" height="7" rx="1.5" fill="#d6402b" />
      <rect x="6" y="16" width="148" height="9" rx="2.5" fill="#e6e3dd" />{Array.from({ length: 14 }, (_, i) => <rect key={i} x={11 + i * 10} y="18.8" width="4.5" height="3.4" rx="0.6" fill={i === 3 ? '#262521' : '#4a4741'} opacity={i === 3 ? 1 : 0.75} />)}
      <rect x="0" y="29" width="160" height="61" fill="#e8e5df" />
      <rect x="40" y="33" width="80" height="57" fill="#fff" stroke="#e3e0d9" stroke-width="0.6" />{[41, 49, 56, 63, 70, 77].map(y => <rect key={y} x="50" y={y} width={y === 41 ? 36 : 60} height={y === 41 ? 4 : 2.6} rx="0.8" fill={y === 41 ? '#1c1b19' : '#bdb8ae'} />)}
      <path d="M103 55.5h5.2a1 1 0 0 1 1 1v2.6a1 1 0 0 1-1 1h-4.4l-1.2 1.2z" fill="#f7d35c" />
    </svg>
  );
  return (
    <div class="ui-choice" role="radiogroup" aria-label="Interface">
      <button type="button" role="radio" aria-checked={cur === 'modern'} class={cur === 'modern' ? 'on' : ''} data-ui-choice="modern" onClick={() => setUiMode('modern')}>
        {modern}<b class="uc-name">Modern</b><span>A single toolbar, with formula, table and picture tools appearing when you need them. The default for new users.</span>
      </button>
      <button type="button" role="radio" aria-checked={cur === 'classic'} class={cur === 'classic' ? 'on' : ''} data-ui-choice="classic" onClick={() => setUiMode('classic')}>
        {classic}<b class="uc-name">Classic</b><span>LyX’s toolbars, menus, names and keys, everything in view — for people coming from desktop LyX.</span>
      </button>
    </div>
  );
}

/**
 * The interface choice on its own: a new account that arrives through the Overleaf import goes
 * straight to its imported document (no welcome card, no tour then). It starts with the account's
 * interface or Modern and offers the optional Classic choice. The tour waits for a later visit.
 */
export function InterfacePrompt({ onDone }: { onDone: () => void }) {
  return (
    <div class="tour" data-tour-step="interface">
      <div class="tour-backdrop" />
      <div class="tour-card interface-prompt" role="dialog" aria-label="Choose the interface" onMouseDown={e => e.preventDefault()}>
        <h3>Welcome to OverLyX 👋</h3>
        <p>Your imported document is ready. Start writing, check the PDF, and share the project when you want to work with others.</p>
        <details><summary>Change the interface</summary><UiChoice /></details>
        <div class="tour-buttons"><button class="btn primary" data-interface-done onClick={onDone}>Continue</button></div>
      </div>
    </div>
  );
}

export function Tour({ ctx, actions, intro, onEnd }: { ctx: TourCtx; actions: TourActions; intro: boolean; onEnd: (how: TourEnd) => void }) {
  const [step, setStep] = useState(intro ? -1 : 0);
  const [base, setBase] = useState<Snap | null>(null);
  /** the task was seen done once — it stays done even if the condition is transient (dialog closed again, cursor left the formula) */
  const [done, setDone] = useState(false);
  const [, setTick] = useState(0);
  const [noExample, setNoExample] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const spotRef = useRef<HTMLDivElement>(null);
  const snap: Snap = { ...ctx, comments: countComments(), styled: countStyled() };
  const cur = step >= 0 ? TOUR_STEPS[step] : null;

  // the interface moves under us (panels open, formulas render): re-measure and re-evaluate regularly
  useEffect(() => { const t = setInterval(() => setTick(x => x + 1), 250); return () => clearInterval(t); }, []);
  // moving between steps resets the snapshot and the task state in the same update (no window with stale ones)
  const go = (n: number) => { setStep(n); setBase(null); setDone(false); };
  useEffect(() => { cur?.enter?.(actions); }, [step]);
  // take the base snapshot once the step is ready (once: base goes from null to a value)
  useEffect(() => { if (cur && !base && (cur.ready?.(snap) ?? true)) setBase(snap); });
  useEffect(() => { if (cur && base && !done && cur.done?.(snap, base)) setDone(true); });

  const task = !!cur?.done;
  const last = step === TOUR_STEPS.length - 1;

  const start = async () => {
    const ok = await actions.openExample();
    if (!ok) { setNoExample(true); return; }
    go(0);
  };

  // The intro is positioned by its intrinsic CSS size: opening its details must
  // not wait for the next tour tick and move a button during a click.
  // Guided steps still follow their target without state or render loops.
  useLayoutEffect(() => {
    const card = cardRef.current, spot = spotRef.current;
    if (!card) return;
    if (step < 0) {
      // A reused card may still carry offsets from the guided steps.
      card.style.removeProperty('left');
      card.style.removeProperty('top');
      return;
    }
    const el = step >= 0 ? findTarget(cur?.target) : null;
    const r = el?.getBoundingClientRect() ?? null;
    const vw = window.innerWidth, vh = window.innerHeight, W = card.offsetWidth, H = card.offsetHeight, m = 12;
    if (spot) {
      if (r) { spot.style.display = 'block'; spot.style.left = r.left - 4 + 'px'; spot.style.top = r.top - 4 + 'px'; spot.style.width = r.width + 8 + 'px'; spot.style.height = r.height + 8 + 'px'; }
      else spot.style.display = 'none';
    }
    let left: number, top: number;
    if (!r) { left = vw - W - 24; top = vh - H - 40; }
    else if (r.height > vh * 0.45) { left = Math.min(r.right, vw) - W - 24; top = Math.min(r.bottom, vh) - H - 40; }   // a big area (the page): sit in its lower right corner
    else if (r.bottom + 10 + H < vh) { left = r.left + r.width / 2 - W / 2; top = r.bottom + 10; }
    else { left = r.left + r.width / 2 - W / 2; top = r.top - H - 10; }
    card.style.left = Math.max(m, Math.min(left, vw - W - m)) + 'px';
    card.style.top = Math.max(m, Math.min(top, vh - H - m)) + 'px';
  });

  const body = cur ? (typeof cur.body === 'function' ? cur.body(snap, base) : cur.body) : null;
  return (
    <div class="tour" data-tour-step={cur?.id ?? 'intro'} data-tour-done={done ? '1' : '0'}>
      {step < 0 && <div class="tour-backdrop" />}
      {step >= 0 && <div class="tour-spot" ref={spotRef} />}
      {/* never take the focus: the user keeps typing in the editor, and Enter/Space there must not activate a tour button */}
      <div class={'tour-card' + (step < 0 ? ' tour-intro' : '')} ref={cardRef} role="dialog" aria-label="Tour" onMouseDown={e => e.preventDefault()}>
        {step < 0 ? (
          <>
            <h3>Welcome to OverLyX 👋</h3>
            <p>Create a document with <b>+ New</b>, or bring an existing project with <b>Import from Overleaf</b>. Write in the editor, check the PDF, and share the project with a colleague.</p>
            <p>The hands-on tour opens an example you can edit and guides you through writing, a formula, a comment, PDF output and sharing.</p>
            <details><summary>Change the interface</summary><UiChoice /></details>
            <p class="tour-muted">Every step can be skipped and you can leave at any time; restart it from <i>Help ▸ Take the tour</i>.</p>
            {noExample && <p class="tour-error">You have no document to practise on — create a project and a document first, or open one shared with you, then start the tour from the Help menu.</p>}
            <div class="tour-buttons">
              <button class="btn" onClick={() => onEnd('declined')}>Not now</button>
              <button class="btn primary" onClick={() => void start()}>Take the tour</button>
            </div>
          </>
        ) : (
          <>
            <div class="tour-head">
              <h3>{cur!.title}</h3>
              <span class="tour-count">{step + 1} / {TOUR_STEPS.length}</span>
              <button class="tour-close" title="End the tour" onClick={() => onEnd('left')}>✕</button>
            </div>
            <div class="tour-body">{body}</div>
            {task && <div class={'tour-task' + (done ? ' done' : '')}>{done ? '✓ Done — go on when you are ready' : base ? 'Try it — the tour notices when you have' : 'Waiting for the document…'}</div>}
            <div class="tour-buttons">
              <button class="btn" disabled={step === 0} onClick={() => go(step - 1)}>Back</button>
              <span class="tour-dots">{TOUR_STEPS.map((s, i) => <i key={s.id} class={i === step ? 'on' : i < step ? 'past' : ''} />)}</span>
              {last ? <button class="btn primary" onClick={() => onEnd('finished')}>Finish</button>
                : task && !done ? <button class="btn" onClick={() => go(step + 1)} title="Go on without doing this">Skip step</button>
                : <button class="btn primary" onClick={() => go(step + 1)}>Next</button>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Whether the tour should be offered on this browser (never seen, not declined). */
export function tourWanted(): boolean { try { return !localStorage.getItem('ol.tour'); } catch { return false; } }
export function rememberTour(how: TourEnd): void { try { localStorage.setItem('ol.tour', how); } catch { /* ignore */ } }
